import { type ReactNode, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import {
  Alert,
  AvatarGroup,
  Box,
  Button,
  Card,
  DialogContent,
  Stack,
  type SxProps,
  type Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { Mail, MapPin, Phone, User } from 'lucide-react';

import {
  CmaGenerationProgressCard,
  CmaGenerationProgressMessage,
} from '@modules/_shared/components/CmaGenerationProgress';
import { ConfirmDialog } from '@modules/_shared/components/ConfirmDialog';
import { LeadUpdatedTimeAgo } from '@modules/_shared/components/lead/LeadUpdatedTimeAgo';
import { LoadingSpinner } from '@modules/_shared/components/LoadingSpinner';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import {
  COMPACT_USER_AVATAR_SIZE,
  UserAvatar,
} from '@modules/_shared/components/UserAvatar';
import { useAutoScrollResultIntoView } from '@modules/_shared/hooks/useAutoScrollResultIntoView';
import {
  type LeadAtAddress,
  useLeadsByAddress,
} from '@modules/_shared/hooks/useLeadsByAddress';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { type AddressSearchInput } from '@modules/_shared/utils/addressSearch';
import { getContactDisplayNames } from '@modules/_shared/utils/contact';
import { formatAddress, formatPhone } from '@modules/_shared/utils/formatting';
import { getLeadQualificationSummary } from '@modules/_shared/utils/lead-labels';
import { userCanViewLeadDetails } from '@modules/_shared/utils/listingPermissions';
import { useLocale } from '@shared/hooks/locale';

type LeadsAtAddressSuggestionsProps = {
  address: AddressSearchInput | null | undefined;
  onSelect: (lead: LeadAtAddress) => void;
  creatingLeadId?: string | null;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  warnAboutExistingListings?: boolean;
};

export const LeadsAtAddressSuggestions = ({
  address,
  onSelect,
  creatingLeadId,
  disabled = false,
  sx,
  warnAboutExistingListings = false,
}: LeadsAtAddressSuggestionsProps) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { leads, loading, error: searchError } = useLeadsByAddress(address);
  const isCreating = creatingLeadId != null;
  const resultsRef = useAutoScrollResultIntoView<HTMLDivElement>({
    isActive: isMobile && leads.length > 0,
    resultKey: 'results',
    resetWhen: leads.length === 0,
    block: 'start',
  });
  const [leadPendingConfirmation, setLeadPendingConfirmation] =
    useState<LeadAtAddress | null>(null);

  const [dismissedError, setDismissedError] = useState<ErrorLike | null>(null);
  const errorSnackbar = (
    <MutationErrorSnackbar
      error={searchError === dismissedError ? null : searchError}
      onClose={() => setDismissedError(searchError)}
    />
  );

  const handleSelect = (lead: LeadAtAddress) => {
    if (warnAboutExistingListings && lead.lots.length > 0) {
      setLeadPendingConfirmation(lead);
      return;
    }

    onSelect(lead);
  };

  const handleConfirmSelection = () => {
    const lead = leadPendingConfirmation;
    setLeadPendingConfirmation(null);

    if (lead != null) {
      onSelect(lead);
    }
  };

  // Only show the spinner when there is nothing to display yet. With
  // cache-and-network, `loading` is also true during a background refetch, so
  // gating on empty results keeps cached cards visible while revalidating.
  if (loading && leads.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
          gap: 2,
        }}
      >
        <LoadingSpinner sx={{ p: 0 }} />
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('Searching for leads at this address...')}
        </Typography>
      </Box>
    );
  }

  if (leads.length === 0) {
    return errorSnackbar;
  }

  return (
    <Box
      ref={resultsRef}
      sx={[
        { scrollMarginTop: { xs: '112px', sm: 0 } },
        ...(Array.isArray(sx) ? sx : sx != null ? [sx] : []),
      ]}
    >
      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
        {t('Existing leads at this address')}
      </Typography>
      <Grid container spacing={2}>
        {leads.map(lead => {
          const canViewLeadDetails = userCanViewLeadDetails(me, lead);
          const { displayName: contactName } = getContactDisplayNames(
            lead,
            canViewLeadDetails,
          );
          const qualification = getLeadQualificationSummary(t, lead);
          const rawPhoneNumber = canViewLeadDetails
            ? lead.contact?.phone_numbers[0]?.number ?? lead.phone_number
            : null;
          const phoneNumber = formatPhone(rawPhoneNumber) ?? rawPhoneNumber;
          const email = canViewLeadDetails
            ? lead.contact?.emails[0]?.email ?? lead.email_address ?? null
            : null;
          const isSelectedLead = creatingLeadId === lead.id;
          const content = (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontWeight: 'bold',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {contactName || t('Unknown contact')}
                </Typography>
                <Typography
                  variant="caption"
                  component="div"
                  sx={{
                    color: 'text.secondary',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  <LeadUpdatedTimeAgo lead={lead} showBothDate />
                </Typography>
              </Box>
              <Stack sx={{ gap: 1, flex: 1 }}>
                <LeadDetail icon={<MapPin size={16} />}>
                  {formatAddress(lead.property ?? {}, ', ')}
                </LeadDetail>
                {qualification && (
                  <LeadDetail icon={<User size={16} />}>
                    {qualification}
                  </LeadDetail>
                )}
                <LeadDetail icon={<Phone size={16} />}>
                  {phoneNumber || '-'}
                </LeadDetail>
                <LeadDetail icon={<Mail size={16} />}>
                  {email || '-'}
                </LeadDetail>
              </Stack>
              {warnAboutExistingListings && lead.lots.length > 0 && (
                <Alert severity="warning" sx={{ py: 0 }}>
                  {t('A listing is already linked to this lead.')}
                </Alert>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isSelectedLead ? (
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <CmaGenerationProgressMessage isLoading minHeight={30} />
                  </Box>
                ) : (
                  <Button
                    variant="contained"
                    fullWidth
                    size="small"
                    disabled={disabled || isCreating}
                    onClick={() => handleSelect(lead)}
                  >
                    {t('Select lead')}
                  </Button>
                )}
                {lead.lead_agents.length > 0 && (
                  <AvatarGroup
                    max={3}
                    sx={{
                      flexShrink: 0,
                      '& .MuiAvatar-root': {
                        width: COMPACT_USER_AVATAR_SIZE,
                        height: COMPACT_USER_AVATAR_SIZE,
                        fontSize: COMPACT_USER_AVATAR_SIZE * 0.43,
                      },
                    }}
                  >
                    {lead.lead_agents.map(leadAgent => (
                      <UserAvatar
                        key={leadAgent.id}
                        user={leadAgent.user}
                        size={COMPACT_USER_AVATAR_SIZE}
                      />
                    ))}
                  </AvatarGroup>
                )}
              </Box>
            </>
          );

          return (
            <Grid key={lead.id} size={{ xs: 12, sm: 6 }}>
              {isSelectedLead ? (
                <CmaGenerationProgressCard>{content}</CmaGenerationProgressCard>
              ) : (
                <Card
                  elevation={0}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    p: 2,
                    height: '100%',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {content}
                </Card>
              )}
            </Grid>
          );
        })}
      </Grid>
      {errorSnackbar}
      <ConfirmDialog
        open={leadPendingConfirmation != null}
        title={t('Create another listing?')}
        onClose={() => setLeadPendingConfirmation(null)}
        onSubmit={handleConfirmSelection}
        okText={t('Create another listing')}
      >
        <DialogContent>
          <Typography>
            {t(
              'This lead is already linked to a listing. Do you want to create another listing?',
            )}
          </Typography>
        </DialogContent>
      </ConfirmDialog>
    </Box>
  );
};

const LeadDetail = ({
  icon,
  children,
}: {
  icon: ReactNode;
  children: ReactNode;
}) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
    <Box
      sx={{
        color: 'text.secondary',
        flexShrink: 0,
        display: 'flex',
        mt: '3px',
      }}
    >
      {icon}
    </Box>
    <Typography
      variant="body2"
      sx={{ color: 'text.secondary', overflowWrap: 'anywhere' }}
    >
      {children}
    </Typography>
  </Box>
);
